import { useEffect, useRef, type ReactNode } from 'react';

import { Icon, type IconName } from './Icon';

export interface ModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  tone?: 'default' | 'danger';
  icon?: IconName;
  footer?: ReactNode;
  size?: 'md' | 'lg';
}

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  tone = 'default',
  icon,
  footer,
  size = 'md',
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || el.open) return;
    el.showModal();
    return () => {
      if (el.open) el.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      aria-label={title}
      className={[
        'm-auto max-h-[calc(100vh-2rem)] rounded-[var(--radius-lg)] border-0 p-0',
        'shadow-[var(--shadow-xl)] backdrop:bg-plum-950/50',
        size === 'lg' ? 'w-[min(760px,calc(100vw-2rem))]' : 'w-[min(440px,calc(100vw-2rem))]',
      ].join(' ')}
    >
      <div className="flex max-h-[calc(100vh-2rem)] flex-col">
        <header className="flex items-start gap-3 border-b border-line px-5 py-4">
          {icon ? (
            <span
              className={`grid h-9 w-9 shrink-0 place-items-center rounded-[10px] ${
                tone === 'danger' ? 'bg-red-100 text-red-700' : 'bg-plum-100 text-plum-700'
              }`}
            >
              <Icon name={icon} size={17} />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <h2 className="text-h3 truncate text-plum-900">{title}</h2>
            {subtitle ? (
              <p className="text-caption truncate text-ink-500">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mt-0.5 -mr-1 grid h-8 w-8 shrink-0 place-items-center rounded-[8px] text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-800"
          >
            <Icon name="close" size={16} title="Close" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="flex gap-3 border-t border-line px-5 py-4">{footer}</div>
        ) : null}
      </div>
    </dialog>
  );
}
